/**
 * #996 — the browser was the only MLS participant with no PRE-SEND epoch check.
 *
 * ## What happened
 *
 * Room `007` was at epoch 22 on 2026-08-22. The owner's browser encrypted and
 * sent at epoch **20**:
 *
 *     17:09:44  chris (owner)  epoch 20
 *     17:09:46  ohtani         epoch 22
 *     17:09:52  smartcode      epoch 22
 *
 *     WARNING mls stale-epoch sender: device=00acd57f msg_epoch=20
 *             group_epoch=22 behind_by=2
 *
 * The message was stored and relayed anyway. It happened to be readable only
 * because every current member had also been present at epoch 20 — the same
 * accident that made a diverged `smartcode` look healthy for a whole morning.
 * The documented remedy was a hard refresh: a human doing by hand what the
 * agents do for themselves.
 *
 * `use-room-chat.ts` had FOUR resync triggers — init mismatch, and three
 * decrypt failures — and **none of them ran before sending**. Hermes has had a
 * pre-send check since #440:
 *
 *     hint = self._server_epoch_hint.get(conversation_id)
 *     if hint is not None and state.epoch < hint:
 *         await self._resync_on_divergence(...)
 *     ciphertext = state.encrypt(plaintext)
 *
 * This module is the web equivalent of that `_server_epoch_hint`.
 *
 * ## Why the threshold differs from `classifyEpochDivergence`
 *
 * That helper deliberately does NOT flag a one-epoch lag: it drives the INIT
 * gate, where acting means tearing down group state and re-joining, and a
 * single missing commit is usually already in flight.
 *
 * The pre-send check is the opposite trade. Acting costs ONE `GET /sync` that
 * returns nothing when there is nothing to replay, while NOT acting sends
 * ciphertext the group may be unable to read — and does so silently, with the
 * server storing and relaying it and every signal reading 200. So this matches
 * Hermes instead: **any** strict lag is worth a catch-up attempt.
 */

/**
 * Fold an epoch seen on an inbound frame into the running hint.
 *
 * Every inbound frame — commit, welcome or application — carries the epoch the
 * group was at when it was produced, so each is a LOWER BOUND on the group's
 * current epoch. The hint is therefore monotonic: it only ever rises.
 *
 * Anything unusable (missing, non-numeric, negative, fractional) is ignored
 * rather than defaulted. A defaulted epoch here would be a guess, and a guess
 * that reads LOW silently disables the check it exists to drive.
 */
export function noteObservedEpoch(current: number, seen: unknown): number {
  const n = Number(seen);
  if (!Number.isInteger(n) || n < 0) return current;
  return n > current ? n : current;
}

/**
 * Should this device try to catch up before it encrypts?
 *
 * True only when the group is known to have moved PAST us. Being AHEAD of the
 * hint is normal and must not trigger anything: we advance our own epoch the
 * moment our commit is confirmed (#972/#980), while the hint only rises when
 * someone else's frame reaches us.
 */
export function needsPreSendResync(
  localEpoch: number,
  observedEpoch: number,
): boolean {
  if (!Number.isInteger(localEpoch) || !Number.isInteger(observedEpoch)) {
    return false;
  }
  return observedEpoch > localEpoch;
}
